import { useEffect, useState } from "react";
import { api, formatApiError } from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

const emptyManual = {
  name: "",
  email: "",
  phone: "",
  amount: 0,
  currency: "NGN",
  payment_type: "summit_registration",
  status: "successful",
  notes: "",
};

const TYPE_LABELS = {
  summit_registration: "Summit Registration",
  donation: "Donation",
  manual: "Manual",
};

const STATUS_COLORS = {
  successful: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  abandoned: "bg-gray-100 text-gray-600",
  reversed: "bg-purple-100 text-purple-700",
};

export default function PaymentsAdmin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyManual);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/api/admin/payments"),
        api.get("/api/admin/payments/stats"),
      ]);
      setItems(list);
      setStats(s);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = items.filter((p) => {
    if (filter === "all") return true;
    if (["summit_registration", "donation"].includes(filter)) return p.payment_type === filter;
    return p.status === filter;
  });

  const remove = async (id) => {
    if (!window.confirm("Delete this payment record? This does not refund the payer.")) return;
    try {
      await api.delete(`/api/admin/payments/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.name || !form.amount) { toast.error("Name and amount required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        amount: Number(form.amount),
        currency: form.currency,
        payment_type: form.payment_type,
        status: form.status,
        notes: form.notes,
      };
      if (form.id) {
        const { data } = await api.put(`/api/admin/payments/${form.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      } else {
        const { data } = await api.post("/api/admin/payments", payload);
        setItems((arr) => [data, ...arr]);
      }
      setOpen(false);
      toast.success("Saved");
      // refresh stats
      api.get("/api/admin/payments/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const exportCsv = () => {
    const headers = ["Reference", "Name", "Email", "Phone", "Type", "Amount", "Currency", "Status", "Channel", "Source", "Created"];
    const rows = items.map((p) => [
      p.reference, p.name, p.email, p.phone,
      TYPE_LABELS[p.payment_type] || p.payment_type,
      p.amount, p.currency, p.status, p.channel || "", p.source || "", p.created_at || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const filters = [
    { k: "all", label: "All" },
    { k: "summit_registration", label: "Summit Registration" },
    { k: "donation", label: "Donation" },
    { k: "successful", label: "Successful" },
    { k: "pending", label: "Pending" },
    { k: "failed", label: "Failed" },
  ];

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-serif font-bold">Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Real Paystack transactions + manual entries.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
          <Button onClick={() => { setForm(emptyManual); setOpen(true); }} className="bg-primary hover:bg-primary-dark text-white" data-testid="new-payment-btn">
            <Plus className="w-4 h-4 mr-2" /> Add manual
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-row">
          <StatCard label="Total successful" value={stats.total_successful} count={stats.count_successful} tone="green" />
          <StatCard label="Summit revenue" value={stats.total_summit} count={stats.count_summit} tone="primary" />
          <StatCard label="Donations total" value={stats.total_donations} count={stats.count_donations} tone="accent" />
          <StatCard label="Pending" value={stats.total_pending} count={stats.count_pending} tone="amber" />
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`filter-${f.k}`}
            className={`px-3 py-1 rounded-full text-xs ${filter === f.k ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">No payments.</Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Channel</th>
                <th className="text-left p-3">Reference</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className="border-t" data-testid={`payment-row-${p.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{p.name}{p.anonymous ? " (anon)" : ""}</div>
                    <div className="text-xs text-muted-foreground">{p.email}</div>
                  </td>
                  <td className="p-3 text-xs">{TYPE_LABELS[p.payment_type] || p.payment_type}</td>
                  <td className="p-3 font-medium">{p.currency || "NGN"} {Number(p.amount || 0).toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLORS[p.status] || "bg-gray-100 text-gray-700"}`}>{p.status}</span>
                  </td>
                  <td className="p-3 text-xs capitalize">{p.channel || "—"}</td>
                  <td className="p-3 font-mono text-[11px] text-muted-foreground truncate max-w-[160px]" title={p.reference}>{p.reference || "—"}</td>
                  <td className="p-3 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => { setForm({ ...emptyManual, ...p }); setOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="text-destructive ml-1" onClick={() => remove(p.id)}><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{form.id ? "Edit payment" : "Add manual payment"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="payment-name" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Amount *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" /></div>
              <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
              <div><Label>Type</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={form.payment_type} onChange={(e) => setForm({ ...form, payment_type: e.target.value })}>
                  <option value="summit_registration">Summit Registration</option>
                  <option value="donation">Donation</option>
                  <option value="manual">Manual / Other</option>
                </select>
              </div>
              <div><Label>Status</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="payment-status">
                  <option value="successful">Successful</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="abandoned">Abandoned</option>
                  <option value="reversed">Reversed</option>
                </select>
              </div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-dark text-white" data-testid="save-payment-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ label, value, count, tone = "primary" }) => {
  const tones = {
    green: "border-green-200",
    primary: "border-primary/30",
    accent: "border-accent/30",
    amber: "border-amber-200",
  };
  return (
    <Card className={`p-4 border ${tones[tone]}`}>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="text-2xl font-serif font-bold">₦{Number(value || 0).toLocaleString()}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{count ?? 0} transactions</div>
    </Card>
  );
};
